import React from "react";
import { CataloguePath, exerciseEditPath } from "routes/createContent/paths";
import { ExerciseListItem, useGetExercises } from "routes/createContent/api/useGetExercises";
import { useDeleteExercise } from "routes/createContent/api/useDeleteExercise";
import { exerciseImage } from "routes/createContent/utils/exerciseHelpers";
import {
  CatalogueList,
  catalogueQuery,
  jointsColumn,
  jointsFilter,
  useCatalogueListControls,
} from "routes/createContent/components/CatalogueList";

const positions = ["standing", "sitting", "lying"];

const movements = [
  "flexion",
  "extension",
  "adduction",
  "abduction",
  "internal_rotation",
  "external_rotation",
  "lateral_flexion",
];

const equipments = ["none", "band", "weight", "ball", "chair", "wall", "mat", "stick", "towel"];

type ExercisesProps = {
  pickerMode?: boolean;
  checkedIds?: number[];
  onPickToggle?: (exercise: ExerciseListItem) => void;
  onPreview?: (exerciseId: number) => void;
};

export const Exercises: React.VFC<ExercisesProps> = props => {
  const controls = useCatalogueListControls({
    inUrl: !props.pickerMode,
    filterKeys: ["joints", "positions", "movements", "equipments"],
  });
  const query = useGetExercises({
    ...catalogueQuery(controls),
    joints: controls.multi("joints"),
    positions: controls.multi("positions"),
    movements: controls.multi("movements"),
    equipments: controls.multi("equipments"),
  });

  return (
    <CatalogueList
      {...props}
      kind="exercise"
      title="Exercises"
      noun="exercise"
      newPath={`${CataloguePath.Exercises}/new`}
      editPath={exerciseEditPath}
      controls={controls}
      query={query}
      deletion={useDeleteExercise()}
      toItem={exercise => ({
        id: exercise.exercise_id,
        name: exercise.name,
        title: exercise.levels[0]?.title ?? "",
        image: exerciseImage(exercise.levels[0]),
        target_joints: exercise.target_joints,
        updated_at: exercise.updated_at,
        deleted_at: exercise.deleted_at,
        creator_id: exercise.creator_id,
        rollout: exercise.rollout,
      })}
      extraColumns={[
        jointsColumn<ExerciseListItem>(exercise => exercise.target_joints),
        { key: "position", label: "Position", width: 130, value: exercise => exercise.levels[0]?.position },
        { key: "movement", label: "Movement", width: 150, value: exercise => exercise.levels[0]?.movement },
      ]}
      extraFilters={[
        jointsFilter(controls),
        {
          label: "Position",
          options: positions,
          value: controls.multi("positions"),
          onChange: controls.setMulti("positions"),
        },
        {
          label: "Movement",
          options: movements,
          value: controls.multi("movements"),
          onChange: controls.setMulti("movements"),
        },
        {
          label: "Equipment",
          options: equipments,
          value: controls.multi("equipments"),
          onChange: controls.setMulti("equipments"),
        },
      ]}
    />
  );
};
